'use client'

import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'

import { getVehMakes, getVehModels, getVehYears } from '@/app/book/actions/find-vehicle'
import { CONTACT_PATHS } from '@/config/contact'
import { MATERIAL_TIER_DEFAULT, MaterialTierPicker } from '@/components/quote/MaterialTierPicker'
import { CompatSelect, ListBoxItem } from '@/components/ui/compat-select'
import { bandValue } from '@/lib/analytics/funnel-events'
import { useFunnelEvents } from '@/lib/analytics/use-funnel-events'
import { useFeatureFlags } from '@/lib/features/feature-flags-context'
import type { MaterialTier } from '@/lib/estimator/pricing/price-book'
import { estimateDetailed } from '@/lib/services/actions/estimator'
import { parsePackageConfig } from '@/lib/validation/service-package-config'

interface ServiceOption {
  id: string
  name: string
  slug: string
  categorySlug: string
  packageConfig?: unknown
}

interface InstantQuoteProps {
  services: ServiceOption[]
}

interface QuoteResult {
  low: number
  high: number
  total: number
  confidence: 'HIGH' | 'MEDIUM' | 'LOW'
}

const fmt = (n: number) => `$${Math.round(n).toLocaleString('en-CA')}`

export function InstantQuote({ services }: InstantQuoteProps) {
  // /quote hands a vehicle over as ?make=&model=&year= ("get a number now
  // while you wait"); apply it once, letting the cascade below load each
  // dependent list in turn.
  const params = useSearchParams()
  const prefill = useRef({
    make: params.get('make') ?? '',
    model: params.get('model') ?? '',
    year: params.get('year') ?? '',
  })

  const [makes, setMakes] = useState<string[]>([])
  const [models, setModels] = useState<string[]>([])
  const [years, setYears] = useState<number[]>([])

  const [make, setMake] = useState(prefill.current.make)
  const [model, setModel] = useState('')
  const [year, setYear] = useState('')
  const [serviceId, setServiceId] = useState(services[0]?.id ?? '')

  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<QuoteResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [materialTier, setMaterialTier] = useState<MaterialTier>(MATERIAL_TIER_DEFAULT)

  const { push } = useFunnelEvents()
  const quoteStartedFired = useRef(false)

  const selectedService = useMemo(
    () => services.find((s) => s.id === serviceId),
    [services, serviceId]
  )
  const selectedIsFullWrap = parsePackageConfig(selectedService?.packageConfig)?.kind === 'full_wrap'

  useEffect(() => {
    getVehMakes().then(setMakes).catch(() => setMakes([]))
  }, [])

  useEffect(() => {
    setModel('')
    setYear('')
    setModels([])
    setYears([])
    if (make)
      getVehModels(make)
        .then((list) => {
          setModels(list)
          const wanted = prefill.current.model
          if (wanted && list.includes(wanted)) {
            prefill.current.model = ''
            setModel(wanted)
          }
        })
        .catch(() => setModels([]))
  }, [make])

  useEffect(() => {
    setYear('')
    setYears([])
    if (make && model)
      getVehYears(make, model)
        .then((list) => {
          setYears(list)
          const wanted = Number(prefill.current.year)
          if (wanted && list.includes(wanted)) {
            prefill.current.year = ''
            setYear(String(wanted))
          }
        })
        .catch(() => setYears([]))
  }, [make, model])

  const { bookingEnabled } = useFeatureFlags()

  // Hand the same vehicle (and the chosen service) to the human quote form.
  const humanQuoteHref = useMemo(() => {
    const q = new URLSearchParams()
    if (make) q.set('make', make)
    if (model) q.set('model', model)
    if (year) q.set('year', year)
    if (selectedService?.slug) q.set('service', selectedService.slug)
    const qs = q.toString()
    return qs ? `${CONTACT_PATHS.quote}?${qs}` : CONTACT_PATHS.quote
  }, [make, model, year, selectedService])

  const canSubmit = Boolean(make && model && year && serviceId) && !loading

  async function handleSubmit() {
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const res = await estimateDetailed({
        make,
        model,
        year: parseInt(year, 10),
        serviceIds: [serviceId],
        materialTier: selectedIsFullWrap ? materialTier : undefined,
      })
      if (res.success && res.data) {
        setResult({
          low: res.data.price.rangeLowCAD,
          high: res.data.price.rangeHighCAD,
          total: res.data.price.totalCAD,
          confidence: res.data.confidence.overall,
        })
        const svc = services.find((s) => s.id === serviceId)
        push({
          event: 'quote_completed',
          service_slugs: svc ? [svc.slug] : [],
          vehicle_class: res.data.vehicle.vehicleType,
          confidence: res.data.confidence.overall,
          value_band: bandValue(res.data.price.totalCAD),
        })
      } else {
        setError(res.error || 'We could not estimate that vehicle. Try another, or request a quote from a person below.')
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='space-y-4 rounded-xl border border-border bg-background p-5'>
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
        <CompatSelect
          label='Make'
          placeholder='Select make'
          selectedKeys={make ? [make] : []}
          onChange={(e) => setMake(e.target.value)}
        >
          {makes.map((m) => (
            <ListBoxItem key={m} id={m}>{m}</ListBoxItem>
          ))}
        </CompatSelect>

        <CompatSelect
          label='Model'
          placeholder='Select model'
          selectedKeys={model ? [model] : []}
          onChange={(e) => setModel(e.target.value)}
          isDisabled={!make}
        >
          {models.map((m) => (
            <ListBoxItem key={m} id={m}>{m}</ListBoxItem>
          ))}
        </CompatSelect>

        <CompatSelect
          label='Year'
          placeholder='Select year'
          selectedKeys={year ? [year] : []}
          onChange={(e) => setYear(e.target.value)}
          isDisabled={!model}
        >
          {years.map((y) => (
            <ListBoxItem key={String(y)} id={String(y)}>{y}</ListBoxItem>
          ))}
        </CompatSelect>

        <CompatSelect
          label='Service'
          selectedKeys={serviceId ? [serviceId] : []}
          onChange={(e) => {
            const id = e.target.value
            setServiceId(id)
            if (!quoteStartedFired.current) {
              const svc = services.find((s) => s.id === id)
              if (svc) {
                push({ event: 'quote_started', service_slug: svc.slug, category_slug: svc.categorySlug })
                quoteStartedFired.current = true
              }
            }
          }}
        >
          {services.map((s) => (
            <ListBoxItem key={s.id} id={s.id}>{s.name}</ListBoxItem>
          ))}
        </CompatSelect>
      </div>

      {selectedIsFullWrap && (
        <MaterialTierPicker value={materialTier} onChange={setMaterialTier} />
      )}

      <button
        type='button'
        onClick={handleSubmit}
        disabled={!canSubmit}
        className='w-full rounded-full bg-accent px-4 py-2.5 text-sm font-semibold text-accent-foreground disabled:bg-[rgba(28,49,68,.09)] disabled:text-[rgba(28,49,68,.42)] disabled:opacity-100 disabled:cursor-not-allowed'
      >
        {loading ? 'Estimating…' : 'Get my estimate'}
      </button>
      {!canSubmit && !loading && (
        <p className='text-center text-[13px] text-[rgba(28,49,68,.56)]'>
          Select make, model and year to see your estimate.
        </p>
      )}

      {error && <p className='text-sm text-danger'>{error}</p>}

      {result && (
        <div className='rounded-lg border border-border bg-surface p-4 text-center space-y-2'>
          <div className='text-xs uppercase tracking-wide text-muted'>Estimated price</div>
          <div className='text-2xl font-bold text-foreground'>
            {fmt(result.low)} – {fmt(result.high)}
          </div>
          <p className='text-xs text-muted'>
            {result.confidence === 'HIGH'
              ? 'A firm price is confirmed when you book.'
              : result.confidence === 'MEDIUM'
                ? 'Estimated range, confirmed at drop-off.'
                : 'Range estimate. Our team will confirm a firm price within 1 business day.'}
          </p>
          {/* Booking on: the wizard locks the estimate in. Booking off: the
              same range goes to the shop through the photo quote form. */}
          <Link
            href={bookingEnabled ? '/book' : humanQuoteHref}
            className='inline-block rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground'
            data-testid='instant-quote-primary-cta'
          >
            {bookingEnabled ? 'Book this service' : 'Send this estimate to us'}
          </Link>
        </div>
      )}

      {/* The instant number is a range from the price book. Anything outside
          it (custom design, unusual vehicle, partial coverage) goes to a person. */}
      <p className='text-center text-xs text-muted'>
        Need a firm number or something custom?{' '}
        <Link href={humanQuoteHref} className='font-semibold text-foreground underline' data-testid='instant-quote-human-link'>
          Request a quote from a person
        </Link>
        .
      </p>
    </div>
  )
}
